/**
 * HeroesManager.js
 * Manages the state on in-game heroes
 */

import { Game, GameTimer } from "pixi_core";

import { Common } from "./Common";
import { EnemyParticle } from "./particles/EnemyParticle";

import { Hero } from "../Heroes/Hero";
import { BlackPanther } from "../Heroes/BlackPanther";
import { Wasp } from "../Heroes/Wasp";
import { Thor } from "../Heroes/Thor";
import { IronMan } from "../Heroes/IronMan";
import { Hulk } from "../Heroes/Hulk";
import { CaptainMarvel } from "../Heroes/CaptainMarvel";
import { CaptainAmerica } from "../Heroes/CaptainAmerica";
import { BlackWidow } from "../Heroes/BlackWidow";

import {
    BuffDebuff,
    CheatEvents,
    GameEvents,
    Heroes,
    HERO_ABILITY_DESCS,
    DEFAULT_FULL_DAMAGE,
    DEFAULT_WASP_DURATION,
    DEFAULT_CAPTAIN_MARVEL_PIERCE_COUNT,
    DEFAULT_BLACK_WIDOW_TIMER_INCREASE,
    DEFAULT_BLACK_PANTHER_DURATION,
    BubbleColor
} from "../Constants";

export class HeroesManager {
    /**
     * @public
     */
    constructor() {
        // HeroesManager is instantiated once and never destroyed
        Common.heroes = this;

        /** @private  @type {Number} */
        this._totalHp = 0;

        /** @private  @type {Number} */
        this._currentHp = 0;

        /** @private  @type {Hero[]} */
        this._heroes = [];

        /** @private  @type {Number} */
        this._captainAmericaDuration = 0;

        /** @private  @type {Number} */
        this._captainAmericaDamageReduction = DEFAULT_FULL_DAMAGE;

        /** @private  @type {Boolean} */
        this._captainAmericaShieldUsed = false;

        /** @private  @type {Number} */
        this._blackPantherDuration = 0;

        /** @private  @type {Number} */
        this._blackPantherDamageAccumulation = 0;

        /** @private  @type {Number} */
        this._blackPantherDamageReduction = DEFAULT_FULL_DAMAGE;

        /** @private  @type {Number[]} */
        this._blackPantherActivationDamageLevel = [];

        /** @private  @type {Number[]} */
        this._blackPantherMinDamage = [];

        /** @private  @type {Number[]} */
        this._blackPantherMaxDamage = [];

        /** @private @type {Boolean} */
        this._cheatInvincible = false;

        /** @private @type {GameTimer} */
        this._timerHealDelay = null;

        Game.on(GameEvents.eSHOT_MATCH, this._onShotMatch, this);
        Game.on(GameEvents.eHEROES_HIT, this._onHeroesHit, this);
        Game.on(GameEvents.eHEROES_HEAL_BUBBLE, this._onHeroesHealBubble, this);

        Game.on(GameEvents.eHERO_ABILITY_CAPTAIN_AMERICA, this._onCaptainAmericaShield, this);
        Game.on(GameEvents.eHERO_ABILITY_BLACK_PANTHER_SETUP, this._onBlackPantherAbilitySetup, this);
        Game.on(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ACTIVATE, this._onBlackPantherAbilityActivate, this);
        Game.on(GameEvents.eHEROES_ABILITY_DURATION_TICK, this._tickDownAbilities, this);
        Game.on(GameEvents.eHEROES_STUNNED, this._onHeroesStunned, this);

        Game.on(CheatEvents.eAVENGERS_INVINCIBLE, this._onCheatInvincible, this);
        Game.on(CheatEvents.eRECEIVE_DAMAGE, this._onCheatReceiveDamage, this);
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        for (let i = 0; i < this._heroes.length; ++i) {
            this._heroes[i].updateLoop(deltaTime);
        }
    }

    /**
     * @public
     */
    beginLevel() {
        this._currentHp = 0;

        // Instantiate the selected team of heroes
        const selectedTeam = Common.save.getHeroTeam();
        for (const heroName of selectedTeam) {
            const hero = this._createHero(heroName);
            if (hero) {
                this._heroes.push(hero);
                this._currentHp += hero.hp;
                hero.setCheatCharge();
            }
        }

        this._totalHp = this._currentHp;

        // Reset heroes abilities
        this._captainAmericaShieldUsed = false;
        this._captainAmericaDuration = 0;
        this._captainAmericaDamageReduction = DEFAULT_FULL_DAMAGE;
        this._blackPantherDuration = 0;
        this._blackPantherDamageReduction = DEFAULT_FULL_DAMAGE;

        Game.fire(GameEvents.eHEROES_INITIALIZED, this._heroes, this._totalHp);
    }

    /**
     * @private
     * @param {String} name
     * @returns {Hero}
     */
    _createHero(name) {
        const heroData = Common.characters.getStats(name);

        switch (name) {
            case Heroes.eBLACK_PANTHER:
                return new BlackPanther(heroData);
            case Heroes.eBLACK_WIDOW:
                return new BlackWidow(heroData);
            case Heroes.eCAPTAIN_AMERICA:
                return new CaptainAmerica(heroData);
            case Heroes.eCAPTAIN_MARVEL:
                return new CaptainMarvel(heroData);
            case Heroes.eHULK:
                return new Hulk(heroData);
            case Heroes.eIRON_MAN:
                return new IronMan(heroData);
            case Heroes.eTHOR:
                return new Thor(heroData);
            case Heroes.eWASP:
                return new Wasp(heroData);
        }

        console.error("[HeroesManager._createHero] Invalid hero name : " + name);
        return null;
    }

    /**
     * The player made a match with his shot
     * @private
     * @param {BubbleColor} firedBubbleColor
     */
    _onShotMatch(firedBubbleColor) {
        if (firedBubbleColor >= BubbleColor.eSPECIAL) return;
        // Charge up heroes abilities
        for (const hero of this._heroes) {
            hero.chargeAbility();
        }
    }

    /**
     * The heroes have been hit and have received damage
     * @private
     * @param {Number} damage
     * @param {EnemyParticle} particle
     */
    _onHeroesHit(damage, particle) {
        if (this._cheatInvincible) {
            damage = 0;
        }

        // Captain America ability damage reduction
        if (this._captainAmericaDuration > 0) {
            Game.audio.playSfx("IDSND_ABIL_CAPT_AMER_SHEILD_HIT");
            damage *= this._captainAmericaDamageReduction;
            this._captainAmericaShieldUsed = true;
        } else {
            Game.audio.playSfx("IDSND_ATK_IMPACT");
        }

        // Black Panther ability damage reduction and counter attack
        if (this._blackPantherDuration > 0) {
            damage *= this._blackPantherDamageReduction;
            this._blackPantherDamageAccumulation += damage;
        }

        let resultingHp = Math.max(this._currentHp - damage, 0);
        Game.fire(GameEvents.eHEROES_DAMAGED, resultingHp, damage, particle);

        this._currentHp = resultingHp;

        if (this._currentHp === 0) {
            Game.fire(GameEvents.eHEROES_DEAD);
        }
    }

    _onHeroesHealBubble() {
        const heal = Common.settings.healBubbleHealing;
        this._currentHp = Math.min(this._currentHp + heal, this._totalHp);

        if (this._timerHealDelay) {
            this._timerHealDelay.destroy();
            this._timerHealDelay = null;
        }

        this._timerHealDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.animations.healDelay);
        this._timerHealDelay.onComplete = () => {
            Game.fire(GameEvents.eHEROES_HEALED, this._currentHp);
        };
    }

    /**
     * @public
     */
    closeLevel() {
        for (const hero of this._heroes) {
            hero.destroy();
        }
        this._heroes = [];

        if (this._timerHealDelay) {
            this._timerHealDelay.destroy();
            this._timerHealDelay = null;
        }
    }

    /**
     * On Captain America ability activation
     * @private
     * @param {Object} options
     */
    _onCaptainAmericaShield(options) {
        this._captainAmericaDuration = options.abilityDuration;
        this._captainAmericaDamageReduction = (100 - options.damageReduction) / 100;
    }

    /**
     * @public
     * @returns {Boolean}
     */
    isCaptainAmericaShieldActive() {
        return this._captainAmericaDuration > 0;
    }

    /**
     * On Black Panther ability setup
     * @private
     * @param {Number[]} activationDamageLevel
     * @param {Number[]} abilityMinDamage
     * @param {Number[]} abilityMaxDamage
     */
    _onBlackPantherAbilitySetup(activationDamageLevel, abilityMinDamage, abilityMaxDamage) {
        this._blackPantherActivationDamageLevel = activationDamageLevel;
        this._blackPantherMinDamage = abilityMinDamage;
        this._blackPantherMaxDamage = abilityMaxDamage;
    }

    /**
     * On Black Panther ability activation
     * @private
     * @param {Number} abilityDamageReduction
     * @param {Number} abilityDuration
     */
    _onBlackPantherAbilityActivate(abilityDamageReduction, abilityDuration) {
        this._blackPantherDuration = abilityDuration;
        this._blackPantherDamageReduction = (100 - abilityDamageReduction) / 100;
    }

    /**
     * @private
     */
    _tickDownAbilities() {
        this._tickDownCaptainAmericaAbility();

        for (let i = 0; i < this._heroes.length; ++i) {
            this._heroes[i].removeStun();
            Game.fire(GameEvents.eHEROES_REMOVE_STUN, i, this._heroes[i].stunDuration);
        }
    }

    /**
     * @private
     */
    _tickDownCaptainAmericaAbility() {
        if (this._captainAmericaDuration === 0 || !this._captainAmericaShieldUsed) return;

        this._captainAmericaShieldUsed = false;
        --this._captainAmericaDuration;
        Game.fire(GameEvents.eGAME_SET_BUFF_TIMER, BuffDebuff.eSHIELD, this._captainAmericaDuration);

        if (this._captainAmericaDuration === 0) {
            this._captainAmericaDamageReduction = DEFAULT_FULL_DAMAGE;
            Game.fire(GameEvents.eHERO_ABILITY_CAPTAIN_AMERICA_DONE);
        }
    }

    /**
     * @public
     */
    activateBlackPantherAbility() {
        let damageLevel = 0;
        for (let i = 0; i < this._blackPantherActivationDamageLevel.length; ++i) {
            if (this._blackPantherDamageAccumulation >= this._blackPantherActivationDamageLevel[i]) {
                damageLevel = i;
            } else {
                break;
            }
        }

        let randomDamage =
            Math.floor(Math.random() * (this._blackPantherMaxDamage[damageLevel] - this._blackPantherMinDamage[damageLevel])) +
            this._blackPantherMinDamage[damageLevel];

        --this._blackPantherDuration;
        Game.fire(GameEvents.eGAME_SET_BUFF_TIMER, BuffDebuff.eCOUNTER, this._blackPantherDuration);
        Game.fire(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ATTACK, this._blackPantherDuration > 0, randomDamage);
        this._blackPantherDamageAccumulation = 0;

        if (this._blackPantherDuration === 0) this._blackPantherDamageReduction = DEFAULT_FULL_DAMAGE;
    }

    /**
     * @public
     * @returns {Boolean}
     */
    checkBlackPantherAbility() {
        return !(this._blackPantherDamageAccumulation === 0 || this._blackPantherDuration === 0);
    }

    /**
     * @private
     * @param {Boolean[]} heroesStunned
     * @param {Number} duration
     */
    _onHeroesStunned(heroesStunned, duration) {
        for (let i = 0; i < heroesStunned.length; ++i) {
            if (heroesStunned[i]) {
                this._heroes[i].stunHero(i, duration);
            }
        }
    }

    /**
     * @public
     * @param {Number} id
     * @returns {Hero}
     */
    getHero(id) {
        return this._heroes[id];
    }

    /**
     * @public
     * @returns {Number}
     */
    getTotalHP() {
        return this._totalHp;
    }

    /**
     * @public
     * @param {String} heroName
     * @returns {String}
     */
    getAbilityDescription(heroName) {
        let description = Game.localization.getText(HERO_ABILITY_DESCS[heroName]);
        const heroData = Common.characters.getStats(heroName);

        /** @type {Number} */
        let variableStat;
        switch (heroName) {
            case Heroes.eCAPTAIN_MARVEL:
                variableStat = Game.utils.assertNumber(heroData.PierceCount, DEFAULT_CAPTAIN_MARVEL_PIERCE_COUNT);
                description = description.replace("[%d]", String(variableStat));
                break;

            case Heroes.eWASP:
                variableStat = Game.utils.assertNumber(heroData.AbilityDuration, DEFAULT_WASP_DURATION);
                description = description.replace("[%d]", String(variableStat));
                break;

            case Heroes.eBLACK_WIDOW:
                variableStat = Game.utils.assertNumber(heroData.TimerIncrease, DEFAULT_BLACK_WIDOW_TIMER_INCREASE);
                description = description.replace("[%d]", String(variableStat));
                break;

            case Heroes.eBLACK_PANTHER:
                variableStat = Game.utils.assertNumber(heroData.AbilityDuration, DEFAULT_BLACK_PANTHER_DURATION);
                description = description.replace("[%d]", String(variableStat));
                break;
        }

        return description;
    }

    /**
     * @private
     * @param {Boolean} invincible
     */
    _onCheatInvincible(invincible) {
        this._cheatInvincible = invincible;
    }

    /**
     * Cheat that inflict damage to the heroes (Bypass invincible cheat)
     * @private
     * @param {Number} damage
     */
    _onCheatReceiveDamage(damage) {
        const invincibleStatus = this._cheatInvincible;
        this._cheatInvincible = false;
        this._onHeroesHit(damage, null);
        this._cheatInvincible = invincibleStatus;
    }
}
